function initJinkHome(sectionRoot) {
  if (!sectionRoot || sectionRoot.dataset.jinkInitialized === "true") return;
  sectionRoot.dataset.jinkInitialized = "true";
  const root = sectionRoot;
  const lifecycle = new AbortController();
  const observers = [];
  const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: lifecycle.signal });
root.classList.add("is-enhanced");
const header = sectionRoot.querySelector("[data-header]");
const scrollProgress = sectionRoot.querySelector("[data-scroll-progress]");
const navToggle = sectionRoot.querySelector("[data-nav-toggle]");
const nav = sectionRoot.querySelector("[data-nav]");
const story = sectionRoot.querySelector("[data-scroll-story]");
const cubeCluster = sectionRoot.querySelector("[data-cube-cluster]");
const cubeTiles = Array.from(sectionRoot.querySelectorAll("[data-cube-tile]"));
const floatingPanels = Array.from(sectionRoot.querySelectorAll(".floating-panel"));
const miniCubes = Array.from(sectionRoot.querySelectorAll(".mini-cube"));
const storyDots = Array.from(sectionRoot.querySelectorAll("[data-story-dot]"));
const stepEyebrow = sectionRoot.querySelector("[data-step-eyebrow]");
const stepTitle = sectionRoot.querySelector("[data-step-title]");
const stepCopy = sectionRoot.querySelector("[data-step-copy]");
const systemScroll = sectionRoot.querySelector("[data-system-scroll]");
const mapNodes = Array.from(sectionRoot.querySelectorAll(".map-node"));
const revealItems = Array.from(sectionRoot.querySelectorAll(".reveal"));
const lockStage = sectionRoot.querySelector("[data-lock-stage]");
const lockSteps = Array.from(sectionRoot.querySelectorAll("[data-lock-step]"));
const sensorStage = sectionRoot.querySelector("[data-sensor-stage]");
const sensorCards = Array.from(sectionRoot.querySelectorAll("[data-sensor]"));

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const defaultStorySteps = [
  {
    eyebrow: "One Standard",
    title: "Every device enters the same home language.",
    copy:
      "JINK turns hubs, sensors, curtains, climate and locks into one system built around Matter style interoperability and Apple Home workflows.",
  },
  {
    eyebrow: "Invisible Pairing",
    title: "The product disappears into the room.",
    copy:
      "Pair a compatible device, assign a room, and choose the routines that fit your day.",
  },
  {
    eyebrow: "Whole-home Matrix",
    title: "Lighting, shades, climate and security move as one.",
    copy:
      "Bring compatible lighting, shades and comfort devices into the same home platform.",
  },
  {
    eyebrow: "Premium Projects",
    title: "A fairy-light world, engineered for real installation.",
    copy:
      "Choose the right models, controllers and installation details for your space.",
  },
];

const defaultDevices = {
  motorAir: {
    type: "Curtain Motor",
    title: "JINK Motor Air",
    copy:
      "A quiet curtain motor designed for Matter and Thread smart homes, with smooth movement for Apple Home style scenes.",
    role: "Shade motion",
    scene: "Morning light and privacy scenes",
    image: "jink-motor-air.png",
    alt: "JINK Motor Air curtain motor",
  },
  gateway: {
    type: "Gateway",
    title: "JINK Super Gateway Matter",
    copy: "A compact bridge for Matter style ecosystems, RF control and professional installation paths.",
    role: "Core bridge",
    scene: "Whole-home control",
    image: "gateway-matter.jpg",
    alt: "JINK Super Gateway Matter",
  },
  curtain: {
    type: "Curtain",
    title: "JINK Motor 25",
    copy: "Small, quiet motion for shades and curtains prepared for room scenes and daily automation.",
    role: "Motion device",
    scene: "Morning and evening scenes",
    image: "curtain-motor.jpg",
    alt: "JINK Motor 25 curtain motor",
  },
  climate: {
    type: "Climate",
    title: "Smart AC Controller",
    copy: "A refined wall interface for air conditioning, fan modes and room comfort control.",
    role: "Comfort control",
    scene: "Sleep, away and return home",
    image: "ac-controller.png",
    alt: "JINK Smart AC Controller",
  },
  entry: {
    type: "Entry",
    title: "JINK Lock Air",
    copy: "A slim entry product story for secure access, scenes and Apple Home style living.",
    role: "Secure access",
    scene: "Arrival and away modes",
    image: "lock-air.png",
    alt: "JINK Lock Air smart door lock",
  },
};

let content = {};
try { content = JSON.parse(sectionRoot.querySelector('[data-jink-content]')?.textContent || '{}'); } catch { content = {}; }
const storySteps = content.storySteps || defaultStorySteps;
const devices = content.devices || defaultDevices;

const lockSeries = {
  deadbolt: {
    type: "Deadbolt lock",
    title: "Deadbolt Lock",
    copy: "A focused access product for main entrances where keypad, fingerprint and scene triggers matter most.",
    fit: "Main entrance",
    role: "Flagship access point",
    scene: "Arrive home, away mode, guest access",
    focusX: 18,
    focusY: 66,
    focusScale: 1.075,
  },
  rim: {
    type: "Rim lock",
    title: "Rim Lock",
    copy: "A retrofit-friendly format for apartment doors, rental upgrades and projects that need fast installation.",
    fit: "Retrofit apartments",
    role: "Fast upgrade path",
    scene: "Rental access and temporary codes",
    focusX: 42,
    focusY: 70,
    focusScale: 1.09,
  },
  handle: {
    type: "Handle lever lock",
    title: "Handle Lever Lock",
    copy: "A full handle replacement story for premium doors that need the lock to feel integrated, not added on.",
    fit: "Premium main doors",
    role: "Integrated handle system",
    scene: "Family access and entry scenes",
    focusX: 56,
    focusY: 58,
    focusScale: 1.105,
  },
  ball: {
    type: "Ball lock",
    title: "Ball Lock",
    copy: "A compact format for interior rooms, service areas and specialty spaces that still need smart access.",
    fit: "Interior and service doors",
    role: "Compact smart access",
    scene: "Room-level permission control",
    focusX: 70,
    focusY: 66,
    focusScale: 1.12,
  },
  cylinder: {
    type: "Cylinder lock",
    title: "Cylinder Lock",
    copy: "A precise cylinder format for retrofit projects where the door hardware should stay minimal.",
    fit: "Cylinder retrofit",
    role: "Minimal hardware upgrade",
    scene: "Project replacement and access control",
    focusX: 88,
    focusY: 72,
    focusScale: 1.13,
  },
};

const sensorSeries = {
  contact: {
    type: "Door and Window",
    title: "JINK Contact Sensor",
    copy: "A compact open-close sensor for doors, windows and cabinet scenes, designed to disappear into the room.",
    role: "Open and close status",
    scene: "Entry, security and ventilation",
    image: "jink-sensor-contact.png",
    alt: "JINK Matter door and window sensor",
    index: 0,
  },
  motionAngle: {
    type: "Motion",
    title: "JINK Motion Sensor",
    copy: "A small room-aware sensor for lighting, corridor and welcome-home automations.",
    role: "Movement detection",
    scene: "Lighting, hallway and night scenes",
    image: "jink-sensor-motion-angle.png",
    alt: "JINK Matter motion sensor",
    index: 1,
  },
  smoke: {
    type: "Safety",
    title: "JINK Smoke Sensor",
    copy: "A clean safety surface for smoke alerts and home protection scenes, presented in the same JINK system language.",
    role: "Smoke alert signal",
    scene: "Safety notification and away mode",
    image: "jink-sensor-smoke.png",
    alt: "JINK Matter smoke sensor",
    index: 2,
  },
  climate: {
    type: "Climate",
    title: "JINK Temperature & Humidity Sensor",
    copy: "A display-based comfort sensor for temperature, humidity and room condition awareness.",
    role: "Temperature and humidity",
    scene: "Comfort, sleep and HVAC scenes",
    image: "jink-sensor-temperature-humidity.png",
    alt: "JINK temperature and humidity sensor",
    index: 3,
  },
  ceilingMotion: {
    type: "Ceiling Presence",
    title: "JINK Ceiling Motion Sensor",
    copy: "A low-profile ceiling sensor for wide-area presence logic in living rooms, corridors and project spaces.",
    role: "Wide-area presence",
    scene: "Room occupancy and energy saving",
    image: "jink-sensor-ceiling-motion.png",
    alt: "JINK ceiling motion sensor",
    index: 4,
  },
};

let activeStoryStep = -1;
let activeLockKey = "";
let activeSensorKey = "";
let lastAutomaticLockKey = "";
let lastAutomaticSensorKey = "";
let pointerX = 0;
let pointerY = 0;
let ticking = false;
let frameId = 0;
let sensorTimer = 0;
let deviceTimer = 0;
let currentScrollY = 0;
let viewportHeight = window.innerHeight;
let lastRects = {};
const motionProperties = new WeakMap();
const hasFinePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

function setMotionProperty(target, name, value) {
  if (!target) return;
  let values = motionProperties.get(target);
  if (!values) {
    values = new Map();
    motionProperties.set(target, values);
  }
  if (values.get(name) === value) return;
  values.set(name, value);
  target.style.setProperty(name, value);
}

function inViewport(rect) {
  return rect && rect.top < viewportHeight && rect.bottom > 0;
}

function clamp(value, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

function easeOut(value) {
  return 1 - Math.pow(1 - value, 3);
}

function resolveAsset(filename) {
  if (/^(?:https?:)?\/\//.test(filename)) return filename;
  const base = sectionRoot.dataset.assetBase || "";
  return base ? `${base}${filename}` : filename;
}

function syncHeader() {
  header?.classList.toggle("is-scrolled", window.scrollY > 18);
}

function syncScrollProgress(maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)) {
  if (!scrollProgress) return;

  const pageProgress = clamp(window.scrollY / maxScroll);
  scrollProgress.style.transform = `scaleX(${pageProgress.toFixed(4)})`;
}

function closeNav() {
  header?.classList.remove("is-open");
  navToggle?.setAttribute("aria-expanded", "false");
}

function setStoryStep(index) {
  if (index === activeStoryStep) return;
  const step = storySteps[index];
  activeStoryStep = index;

  if (stepEyebrow) stepEyebrow.textContent = step.eyebrow;
  if (stepTitle) stepTitle.textContent = step.title;
  if (stepCopy) stepCopy.textContent = step.copy;

  storyDots.forEach((dot, dotIndex) => {
    dot.classList.toggle("is-active", dotIndex === index);
  });
}

function updateStory(rect) {
  if (!story || !inViewport(rect)) return;
  const travel = Math.max(1, rect.height - viewportHeight);
  const progress = clamp(-rect.top / travel);
  const eased = easeOut(progress);
  const stepIndex = clamp(Math.floor(progress * storySteps.length), 0, storySteps.length - 1);

  setMotionProperty(story, "--story-progress", progress.toFixed(4));
  setMotionProperty(story, "--hero-shift", `${Math.round(-130 * eased)}px`);
  setMotionProperty(story, "--hero-opacity", Math.max(0.32, 1 - progress * 0.72).toFixed(3));
  setMotionProperty(story, "--reference-scale", (1.04 + progress * 0.1).toFixed(3));
  setMotionProperty(story, "--reference-y", `${Math.round(progress * -58)}px`);
  setMotionProperty(story, "--story-card-y", `${Math.round(48 - eased * 48)}px`);
  setMotionProperty(story, "--story-card-opacity", (0.88 + eased * 0.12).toFixed(3));
  setMotionProperty(story, "--pointer-x", `${pointerX * 10}px`);
  setMotionProperty(story, "--pointer-y", `${pointerY * 8}px`);
  setMotionProperty(story, "--pointer-x-invert", `${pointerX * -10}px`);
  setMotionProperty(story, "--sky-stream", `${Math.round(progress * -84)}px`);
  setMotionProperty(story, "--sky-depth-a", `${Math.round(progress * -22)}px`);
  setMotionProperty(story, "--sky-depth-b", `${Math.round(progress * 18)}px`);
  setMotionProperty(story, "--arc-opacity", Math.max(0.16, 0.72 - progress * 0.34).toFixed(3));
  setMotionProperty(story, "--cube-shine-x", `${(-36 + progress * 120).toFixed(2)}%`);
  setMotionProperty(story, "--cue-opacity", Math.max(0, 1 - progress * 2).toFixed(3));

  if (cubeCluster) {
    const rotateX = 12 - progress * 15 + pointerY * 4;
    const rotateY = -18 + progress * 32 + pointerX * 5;
    const scale = 1 + progress * 0.08;
    cubeCluster.style.transform = `translate3d(-50%, -50%, 0) rotateX(${rotateX.toFixed(
      2,
    )}deg) rotateY(${rotateY.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
  }

  cubeTiles.forEach((tile, index) => {
    const x = Number(tile.dataset.x || 0);
    const y = Number(tile.dataset.y || 0);
    const z = Number(tile.dataset.z || 0);
    const rx = Number(tile.dataset.rx || 0);
    const ry = Number(tile.dataset.ry || 0);
    const localEase = easeOut(clamp((progress - index * 0.015) / 0.82));
    const float = Math.sin(currentScrollY * 0.006 + index) * 4;
    tile.style.transform = `translate3d(${(x * localEase).toFixed(1)}px, ${(y * localEase + float).toFixed(
      1,
    )}px, ${(z * localEase).toFixed(1)}px) rotateX(${(rx * localEase).toFixed(2)}deg) rotateY(${(
      ry * localEase
    ).toFixed(2)}deg)`;
    tile.style.opacity = (0.94 - progress * 0.12).toFixed(3);
  });

  floatingPanels.forEach((panel, index) => {
    const direction = index === 0 ? -1 : 1;
    panel.style.transform = `translate3d(${direction * eased * 28 + pointerX * 8}px, ${
      Math.sin(currentScrollY * 0.004 + index) * 8 + pointerY * 6
    }px, 0) rotate(${direction * (5 + progress * 4)}deg)`;
  });

  miniCubes.forEach((cube, index) => {
    cube.style.transform = `translate3d(${pointerX * (8 + index * 2)}px, ${
      Math.sin(currentScrollY * 0.005 + index * 1.7) * 18 + pointerY * (7 + index)
    }px, 0) rotate(${currentScrollY * 0.018 + index * 18}deg)`;
  });

  setStoryStep(stepIndex);
}

function updateSystemSequence(rect) {
  if (!systemScroll || !inViewport(rect)) return;
  const travel = Math.max(1, rect.height - viewportHeight);
  const progress = clamp(-rect.top / travel);

  setMotionProperty(systemScroll, "--map-line-one", `${Math.min(54, progress * 110).toFixed(2)}%`);
  setMotionProperty(systemScroll, "--map-line-two", `${Math.max(0, Math.min(62, (progress - 0.38) * 120)).toFixed(2)}%`);
  setMotionProperty(systemScroll, "--system-progress", progress.toFixed(4));
  setMotionProperty(systemScroll, "--system-map-y", `${Math.round((1 - progress) * 18)}px`);
  setMotionProperty(systemScroll, "--system-rotation", `${(progress * 44).toFixed(2)}deg`);
  setMotionProperty(systemScroll, "--system-scale", (0.92 + progress * 0.12).toFixed(3));
  setMotionProperty(systemScroll, "--system-grid-y", `${Math.round(progress * -22)}px`);

  mapNodes.forEach((node, index) => {
    node.classList.toggle("is-lit", progress > index * 0.2 - 0.04);
  });
}

function setLockSeries(key) {
  const stage = sectionRoot.querySelector("[data-lock-stage]");
  const next = lockSeries[key];
  if (!stage || !next || key === activeLockKey) return;

  activeLockKey = key;
  stage.dataset.activeLock = key;
  setMotionProperty(lockStage, "--lock-focus-x", `${next.focusX}%`);
  setMotionProperty(lockStage, "--lock-focus-y", `${next.focusY}%`);
  setMotionProperty(lockStage, "--lock-focus-scale", next.focusScale.toFixed(3));

  const buttons = Array.from(stage.querySelectorAll("[data-lock]"));
  const type = stage.querySelector("[data-lock-type]");
  const title = stage.querySelector("[data-lock-title]");
  const copy = stage.querySelector("[data-lock-copy]");
  const fit = stage.querySelector("[data-lock-fit]");
  const role = stage.querySelector("[data-lock-role]");
  const scene = stage.querySelector("[data-lock-scene]");

  buttons.forEach((item) => {
    const selected = item.dataset.lock === key;
    item.classList.toggle("is-active", selected);
    item.setAttribute("aria-selected", selected ? "true" : "false");
  });

  lockSteps.forEach((item) => {
    item.classList.toggle("is-active", item.dataset.lockStep === key);
  });

  if (type) type.textContent = next.type;
  if (title) title.textContent = next.title;
  if (copy) copy.textContent = next.copy;
  if (fit) fit.textContent = next.fit;
  if (role) role.textContent = next.role;
  if (scene) scene.textContent = next.scene;
}

function updateLockStory(rect) {
  if (!lockStage || !inViewport(rect)) return;
  const travel = Math.max(1, rect.height - viewportHeight);
  const progress = clamp(-rect.top / travel);
  const eased = easeOut(progress);
  const keys = Object.keys(lockSeries);
  const keyIndex = clamp(Math.floor(progress * keys.length), 0, keys.length - 1);
  const activeKey = keys[keyIndex];

  setMotionProperty(lockStage, "--lock-progress", progress.toFixed(4));
  setMotionProperty(lockStage, "--lock-image-y", `${Math.round((0.5 - progress) * 34)}px`);
  setMotionProperty(lockStage, "--lock-image-scale", (1.01 + eased * 0.055).toFixed(3));
  setMotionProperty(lockStage, "--lock-panel-y", `${Math.round((0.5 - progress) * 18)}px`);
  setMotionProperty(lockStage, "--lock-panel-counter-y", `${Math.round((progress - 0.5) * 6)}px`);
  setMotionProperty(lockStage, "--lock-rail-progress", `${Math.min(100, progress * 118).toFixed(2)}%`);
  setMotionProperty(lockStage, "--lock-shine-x", `${(-40 + progress * 70).toFixed(2)}%`);
  setMotionProperty(lockStage, "--lock-focus-ring-scale", (0.82 + progress * 0.18).toFixed(3));

  if (rect.top < viewportHeight * 0.78 && rect.bottom > viewportHeight * 0.2) {
    if (lastAutomaticLockKey !== activeKey) {
      const manualSelection = lastAutomaticLockKey === null;
      lastAutomaticLockKey = activeKey;
      if (!manualSelection) setLockSeries(activeKey);
    }
  }
}

function setSensorSeries(key, immediate = false) {
  const next = sensorSeries[key];
  if (!sensorStage || !next) return;
  if (key === activeSensorKey && !immediate) return;

  window.clearTimeout(sensorTimer);
  activeSensorKey = key;
  sensorStage.dataset.activeSensor = key;
  setMotionProperty(sensorStage, "--sensor-active-index", `${next.index}`);

  const hero = sensorStage.querySelector(".sensor-hero");
  const image = sensorStage.querySelector("[data-sensor-image]");
  const type = sensorStage.querySelector("[data-sensor-type]");
  const title = sensorStage.querySelector("[data-sensor-title]");
  const copy = sensorStage.querySelector("[data-sensor-copy]");
  const role = sensorStage.querySelector("[data-sensor-role]");
  const scene = sensorStage.querySelector("[data-sensor-scene]");

  sensorCards.forEach((item) => {
    const selected = item.dataset.sensor === key;
    item.classList.toggle("is-active", selected);
    item.setAttribute("aria-selected", selected ? "true" : "false");
  });

  const applyContent = () => {
    if (image) {
      image.src = resolveAsset(next.image);
      image.alt = next.alt;
    }
    if (type) type.textContent = next.type;
    if (title) title.textContent = next.title;
    if (copy) copy.textContent = next.copy;
    if (role) role.textContent = next.role;
    if (scene) scene.textContent = next.scene;
    hero?.classList.remove("is-switching");
  };

  if (immediate) {
    applyContent();
    return;
  }

  hero?.classList.add("is-switching");
  sensorTimer = window.setTimeout(applyContent, 150);
}

function updateSensorStory(rect) {
  if (!sensorStage || !inViewport(rect)) return;
  const travel = Math.max(1, rect.height - viewportHeight);
  const progress = clamp(-rect.top / travel);
  const eased = easeOut(progress);
  const keys = Object.keys(sensorSeries);
  const keyIndex = clamp(Math.floor(progress * keys.length), 0, keys.length - 1);

  setMotionProperty(sensorStage, "--sensor-progress", progress.toFixed(4));
  setMotionProperty(sensorStage, "--sensor-image-y", `${Math.round((0.5 - progress) * 28)}px`);
  setMotionProperty(sensorStage, "--sensor-tilt", `${((progress - 0.5) * 4 + pointerX * 3).toFixed(2)}deg`);

  if (rect.top < viewportHeight * 0.86 && rect.bottom > viewportHeight * 0.1) {
    const key = keys[keyIndex];
    if (lastAutomaticSensorKey !== key) {
      const manualSelection = lastAutomaticSensorKey === null;
      lastAutomaticSensorKey = key;
      if (!manualSelection) setSensorSeries(key);
    }
  }
}

function updateScrollEffects() {
  ticking = false;
  if (lifecycle.signal.aborted || document.hidden || !root.isConnected) return;
  viewportHeight = window.innerHeight;
  currentScrollY = window.scrollY;
  const maxScroll = Math.max(1, document.documentElement.scrollHeight - viewportHeight);
  lastRects = prefersReducedMotion ? {} : {
    story: story?.getBoundingClientRect(),
    system: systemScroll?.getBoundingClientRect(),
    lock: lockStage?.getBoundingClientRect(),
    sensor: sensorStage?.getBoundingClientRect(),
  };
  syncHeader();
  syncScrollProgress(maxScroll);
  if (!prefersReducedMotion) {
    updateStory(lastRects.story);
    updateSystemSequence(lastRects.system);
    updateLockStory(lastRects.lock);
    updateSensorStory(lastRects.sensor);
  }
}

function requestScrollUpdate() {
  if (ticking || lifecycle.signal.aborted || document.hidden) return;
  ticking = true;
  frameId = window.requestAnimationFrame(updateScrollEffects);
}

function setupReveals() {
  if (prefersReducedMotion || !("IntersectionObserver" in window)) {
    revealItems.forEach((item) => item.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.01 },
  );

  observers.push(observer);
  revealItems.forEach((item) => observer.observe(item));
}

function setupDeviceSwitcher() {
  const stage = sectionRoot.querySelector("[data-device-stage]");
  if (!stage) return;

  const buttons = Array.from(stage.querySelectorAll("[data-device]"));
  const visual = stage.querySelector(".device-visual");
  const image = stage.querySelector("[data-device-image]");
  const type = stage.querySelector("[data-device-type]");
  const title = stage.querySelector("[data-device-title]");
  const copy = stage.querySelector("[data-device-copy]");
  const role = stage.querySelector("[data-device-role]");
  const scene = stage.querySelector("[data-device-scene]");
  const productLink = stage.querySelector("[data-device-link]");

  listen(stage.querySelector('[role=tablist]'),'keydown',event => {
    if (!['ArrowRight','ArrowLeft','ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
    const at = buttons.indexOf(document.activeElement);
    if(at < 0) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 :
      (at + (['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next].focus(); buttons[next].click();
  });
  buttons.forEach((button) => {
    listen(button, "click", () => {
      const next = devices[button.dataset.device];
      if (!next || button.classList.contains("is-active")) return;

      buttons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle("is-active", selected);
        item.setAttribute("aria-selected", selected ? "true" : "false");
        item.tabIndex = selected ? 0 : -1;
      });

      visual?.classList.add("is-switching");

      window.clearTimeout(deviceTimer);
      deviceTimer = window.setTimeout(() => {
        if (image) {
          image.src = resolveAsset(next.image);
          image.alt = next.alt;
        }
        if (type) type.textContent = next.type;
        if (title) title.textContent = next.title;
        if (copy) copy.textContent = next.copy;
        if (role) role.textContent = next.role;
        if (scene) scene.textContent = next.scene;
        if (productLink) productLink.href = next.url;
        stage.querySelector("[role=tabpanel]")?.setAttribute("aria-labelledby",button.id);
        visual?.classList.remove("is-switching");
      }, 170);
    });
  });
}

function setupLockSeries() {
  const stage = sectionRoot.querySelector("[data-lock-stage]");
  if (!stage) return;

  const buttons = Array.from(stage.querySelectorAll("[data-lock]"));

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      lastAutomaticLockKey = null;
      setLockSeries(button.dataset.lock);
    });
  });
}

function setupSensorSeries() {
  if (!sensorStage) return;

  sensorCards.forEach((button) => {
    button.addEventListener("click", () => {
      lastAutomaticSensorKey = null;
      setSensorSeries(button.dataset.sensor);
    });
  });
}

function setupMagSwitchVideo() {
  const videos = Array.from(sectionRoot.querySelectorAll(".magswitch-media video"));
  if (!videos.length) return;
  const visibleVideos = new Set();
  const saveData = Boolean(navigator.connection?.saveData);

  const playVideo = (video, explicit = false) => {
    if (document.hidden || lifecycle.signal.aborted) return;
    if (!explicit && (prefersReducedMotion || saveData)) return;
    const source = video.querySelector("source[data-src]");
    if (source?.dataset.src) {
      source.src = source.dataset.src;
      delete source.dataset.src;
      video.load();
    }
    video.muted = true;
    video.playsInline = true;
    const media = video.closest(".magswitch-media");
    const attempt = video.play?.();
    if (attempt?.then) {
      attempt.then(() => {
        if (document.hidden || lifecycle.signal.aborted || (!explicit && !visibleVideos.has(video))) {
          video.pause();
          return;
        }
        media?.classList.add("is-playing");
      }).catch(() => media?.classList.remove("is-playing"));
    }
  };

  videos.forEach((video) => {
    const media = video.closest(".magswitch-media");
    listen(media?.querySelector("[data-magswitch-play]"), "click", () => playVideo(video, true));
    listen(video, "play", () => media?.classList.add("is-playing"));
    listen(video, "pause", () => media?.classList.remove("is-playing"));
  });

  listen(document, "visibilitychange", () => {
    videos.forEach((video) => {
      if (document.hidden) video.pause();
      else if (visibleVideos.has(video)) playVideo(video);
    });
  });

  if (!("IntersectionObserver" in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.28) {
        visibleVideos.add(video);
        playVideo(video);
      } else {
        visibleVideos.delete(video);
        video.pause?.();
      }
    });
  }, { threshold: [0, 0.28] });
  observers.push(observer);
  videos.forEach((video) => observer.observe(video));
}

syncHeader();
syncScrollProgress();
setStoryStep(0);
setLockSeries("deadbolt");
setSensorSeries("contact", true);
setupReveals();
setupDeviceSwitcher();
setupLockSeries();
setupSensorSeries();
setupMagSwitchVideo();
requestScrollUpdate();

listen(window, "scroll", requestScrollUpdate, { passive: true });
listen(window, "resize", requestScrollUpdate);

listen(window, 
  "pointermove",
  (event) => {
    if (prefersReducedMotion || !hasFinePointer || (!inViewport(lastRects.story) && !inViewport(lastRects.sensor))) return;
    pointerX = clamp(event.clientX / window.innerWidth - 0.5, -0.5, 0.5);
    pointerY = clamp(event.clientY / window.innerHeight - 0.5, -0.5, 0.5);
    requestScrollUpdate();
  },
  { passive: true },
);

listen(navToggle, "click", () => {
  const isOpen = header?.classList.toggle("is-open");
  navToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
});

listen(nav, "click", (event) => {
  const link = event.target.closest("a");
  if (!link) return;
  closeNav();
});

sectionRoot.querySelectorAll('a[href^="#"]').forEach((link) => {
  link.addEventListener("click", (event) => {
    const target = sectionRoot.querySelector(link.getAttribute("href"));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
  });
});

listen(window, "keydown", (event) => {
  if (event.key === "Escape") closeNav();
});

listen(document, "visibilitychange", () => {
  if (document.hidden) {
    window.cancelAnimationFrame(frameId);
    ticking = false;
  } else requestScrollUpdate();
});
listen(document, "shopify:section:unload", (event) => {
  if (!event.target.contains(root)) return;
  lifecycle.abort();
  observers.forEach((observer) => observer.disconnect());
  window.cancelAnimationFrame(frameId);
  window.clearTimeout(sensorTimer);
  window.clearTimeout(deviceTimer);
  root.querySelectorAll("video").forEach((video) => video.pause());
  delete root.dataset.jinkInitialized;
});

}

function initAllJinkHomes() {
  document.querySelectorAll("[data-jink-home]").forEach(initJinkHome);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAllJinkHomes, { once: true });
} else {
  initAllJinkHomes();
}

document.addEventListener("shopify:section:load", (event) => {
  const sectionRoot = event.target.querySelector("[data-jink-home]");
  if (sectionRoot) initJinkHome(sectionRoot);
});

